import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { FiltrosDaLista } from '../../../api/comprasDashboardApi';
import FiltroDaListaModal, { BotaoFiltrosDaLista, CampoDaLista, contarFiltrosDaLista } from './FiltroDaListaModal';
import { SituacaoDeRequisicao } from '../situacoes';

import '../../../shared/ui/modal-close-button.css';
import '../../../components/modal/add-car/AddCarModal.css';
import './RequisicoesDoRecorteModal.css';

/**
 * O casco dos modais de detalhamento do Dashboard de Compras (at-110).
 *
 * Todo card do painel abre a mesma coisa: um modal com título, contagem, tabela, os quatro estados
 * (carregando, erro, vazio, dados) e paginação. Até aqui isso estava copiado em três arquivos
 * (`RequisicoesDoRecorteModal`, `PedidosDoMesModal`, `TempoAtendimentoModal`), e os dois
 * detalhamentos novos seriam a quarta e a quinta cópia — que é onde o catálogo de UI manda parar e
 * extrair.
 *
 * 🔴 **Os quatro estados não podem se parecer**, e é o que este casco garante por construção:
 * falha de carga NUNCA vira tabela vazia. "Este fornecedor não tem pedido nenhum" é a conclusão
 * oposta à verdadeira, e é exatamente o que um catch silencioso produz (regra da at-60).
 *
 * ⚠️ Nenhuma classe de CSS nova: reusa o overlay compartilhado, o X de `modal-close-button.css` e
 * as classes `dcr-*`. Desde a at-95 os dois modais antigos que sobravam montam sobre este casco, e o
 * terceiro (`TempoAtendimentoModal`) foi removido — ninguém o abria desde a at-116.
 */

interface Props {
  titulo: string;
  /** Uma linha abaixo do título: a contagem, o recorte, o que ficou de fora. */
  legenda: ReactNode;
  carregando: boolean;
  erro: boolean;
  /** O que dizer quando a carga falha. Precisa negar a leitura de "está vazio". */
  erroTexto: string;
  vazio: boolean;
  vazioTexto: string;
  colunas: string[];
  /** Classe extra da grade, quando o modal tem número de colunas diferente do padrão. */
  classeTabela?: string;
  children: ReactNode;
  /** Bloco acima da tabela: os dados da entidade, quando o modal mostra mais que uma lista. */
  antesDaTabela?: ReactNode;
  /**
   * at-129: bloco abaixo da tabela — a seção de comentários do pedido/requisição.
   *
   * ⚠️ Renderizado mesmo com a carga da entidade falhando: os comentários têm carga e estado de
   * erro próprios, e esconder a conversa porque o detalhe do ERP falhou tiraria do comprador
   * justamente o que ele não consegue achar em outro lugar.
   */
  depoisDaTabela?: ReactNode;
  paginacao?: {
    pagina: number;
    ultimaPagina: number;
    onMudar: (pagina: number) => void;
  };
  /** Conteúdo à esquerda da paginação (soma da página, nota de recorte). */
  rodape?: ReactNode;
  /**
   * Segura o fechamento por clique fora e por Escape enquanto um modal FILHO estiver aberto.
   *
   * 🔴 Não é preferência de fluxo: o filho é renderizado **fora do `ref`** deste modal, então o
   * handler de "clicou fora" conta qualquer clique dentro dele como externo e o hospedeiro se
   * fecha no primeiro clique. Os dois listeners de `Escape` também vivem em `document` e são
   * irmãos — `stopPropagation` não separa um do outro. É a armadilha que o `ChecklistModal` já
   * documenta desde a at-58, aqui na quinta ocorrência.
   */
  travarFechamento?: boolean;
  /**
   * Este modal abre SOBRE outro modal.
   *
   * 🔴 Liga um z-index explícito no overlay (`modal-overlay--empilhado`, de
   * `shared/ui/modal-overlay.css`), em vez de contar com a ordem
   * do DOM para ficar por cima: com dois `.modal-overlay` de z-index igual, quem vence é a ordem de
   * renderização — que ninguém controla ao escrever CSS. É a regra que saiu do `BUG-R97`, em que o
   * "Alterar esteira" passou a abrir ATRÁS do modal de detalhes em produção.
   */
  empilhado?: boolean;
  /**
   * transversal/at-90: os filtros DENTRO da lista. O casco desenha o botão "Filtros" no cabeçalho
   * e abre o modal por cima, travando o próprio fechamento enquanto ele existe.
   */
  filtroDaLista?: {
    campos: CampoDaLista[];
    situacoes?: SituacaoDeRequisicao[];
    rotuloDaBusca?: string;
    rotuloDoPeriodo?: string;
    aplicados: FiltrosDaLista;
    onAplicar: (filtros: FiltrosDaLista) => void;
  };
  onFechar: () => void;
}

export default function ModalDeDetalhe({
  titulo,
  legenda,
  carregando,
  erro,
  erroTexto,
  vazio,
  vazioTexto,
  colunas,
  classeTabela,
  children,
  antesDaTabela,
  depoisDaTabela,
  paginacao,
  rodape,
  travarFechamento = false,
  empilhado = false,
  filtroDaLista,
  onFechar,
}: Props) {
  const caixaRef = useRef<HTMLDivElement>(null);
  const [filtroAberto, setFiltroAberto] = useState(false);
  // 🔴 O modal de filtros é renderizado fora do `ref`: sem somá-lo à trava, o primeiro clique nele
  // fecharia a lista (a armadilha que `travarFechamento` documenta).
  const travado = travarFechamento || filtroAberto;

  // Fechar por Escape e por clique fora — o padrão dos modais do projeto.
  useEffect(() => {
    // 🔴 Com um modal filho aberto, os dois caminhos ficam inertes: ver `travarFechamento`.
    if (travado) return undefined;

    const porTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    const porClique = (e: MouseEvent) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('keydown', porTecla);
    document.addEventListener('mousedown', porClique);
    return () => {
      document.removeEventListener('keydown', porTecla);
      document.removeEventListener('mousedown', porClique);
    };
  }, [onFechar, travado]);

  const temPaginacao = paginacao && paginacao.ultimaPagina > 1;

  return (
    <div className={`modal-overlay${empilhado ? ' modal-overlay--empilhado' : ''}`}>
      <div className="dcr-modal" ref={caixaRef} role="dialog" aria-modal="true">
        <div className="dcr-cabecalho">
          <div>
            <h2>{titulo}</h2>
            <p className="dcr-legenda">{legenda}</p>
          </div>
          <div className="dcr-cabecalho-acoes">
            {filtroDaLista ? (
              <BotaoFiltrosDaLista aplicados={filtroDaLista.aplicados} onClick={() => setFiltroAberto(true)} />
            ) : null}
            <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
              &times;
            </button>
          </div>
        </div>

        <div className="dcr-corpo">
          {erro ? (
            // 🔴 Faixa de erro, nunca tabela vazia: ver o cabeçalho deste arquivo.
            <p className="erro-de-tela">{erroTexto}</p>
          ) : carregando ? (
            <p className="dcr-vazio">Carregando...</p>
          ) : (
            <>
              {/* Os dados da entidade vêm ANTES da tabela e aparecem mesmo com a lista vazia: uma
                  requisição sem item continua tendo comprador, placa e justificativa. */}
              {antesDaTabela}
              {vazio ? <p className="dcr-vazio">{vazioTexto}</p> : null}
            </>
          )}
          {!erro && !carregando && !vazio ? (
            <div className="dcr-scroll">
              <div className={`dcr-tabela ${classeTabela ?? ''}`.trim()}>
                <div className="dcr-linha dcr-cabecalho-linha">
                  {colunas.map((c) => (
                    <div key={c} className="dcr-celula">
                      {c}
                    </div>
                  ))}
                </div>
                {children}
              </div>
            </div>
          ) : null}
          {depoisDaTabela}
        </div>

        {!erro && !carregando && !vazio && (rodape || temPaginacao) ? (
          <div className="dcr-paginacao">
            {rodape}
            {temPaginacao ? (
              <>
                <button
                  type="button"
                  disabled={paginacao!.pagina <= 1}
                  onClick={() => paginacao!.onMudar(paginacao!.pagina - 1)}
                >
                  Anterior
                </button>
                <span>
                  Página {paginacao!.pagina} de {paginacao!.ultimaPagina}
                </span>
                <button
                  type="button"
                  disabled={paginacao!.pagina >= paginacao!.ultimaPagina}
                  onClick={() => paginacao!.onMudar(paginacao!.pagina + 1)}
                >
                  Próxima
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Montagem condicional: cada abertura recomeça do que está aplicado (BUG-V43). */}
      {filtroDaLista && filtroAberto ? (
        <FiltroDaListaModal
          campos={filtroDaLista.campos}
          situacoes={filtroDaLista.situacoes}
          rotuloDaBusca={filtroDaLista.rotuloDaBusca}
          rotuloDoPeriodo={filtroDaLista.rotuloDoPeriodo}
          aplicados={filtroDaLista.aplicados}
          onAplicar={(f) => {
            setFiltroAberto(false);
            filtroDaLista.onAplicar(f);
          }}
          onFechar={() => setFiltroAberto(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * A carga paginada de um detalhamento: os três estados e a página, num lugar só.
 *
 * 🔴 O `catch` zera os DADOS e liga o erro — nunca deixa a lista anterior na tela nem a troca por
 * uma vazia. É a metade da regra da at-60 que o casco acima não consegue garantir sozinho.
 */
export function useDetalhePaginado<T>(
  carregarDados: (pagina: number, filtrosDaLista: FiltrosDaLista) => Promise<T>,
) {
  const [dados, setDados] = useState<T | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [pagina, setPagina] = useState(1);
  // at-90: os filtros DENTRO da lista. Vivem aqui para todo detalhamento ter o mesmo ciclo.
  const [filtrosDaLista, setFiltrosDaLista] = useState<FiltrosDaLista>({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(false);
    try {
      setDados(await carregarDados(pagina, filtrosDaLista));
    } catch {
      setDados(null);
      setErro(true);
    } finally {
      setCarregando(false);
    }
  }, [carregarDados, pagina, filtrosDaLista]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Aplicar volta para a página 1: a página 3 do recorte anterior seria uma lista vazia sem motivo. */
  const aplicarFiltrosDaLista = useCallback((f: FiltrosDaLista) => {
    setFiltrosDaLista(f);
    setPagina(1);
  }, []);

  return {
    dados,
    carregando,
    erro,
    pagina,
    setPagina,
    filtrosDaLista,
    aplicarFiltrosDaLista,
    filtrado: contarFiltrosDaLista(filtrosDaLista) > 0,
  };
}

/** A frase que a legenda acrescenta quando a lista está filtrada — diz que o total é do recorte. */
export const SUFIXO_FILTRADO = ' · lista filtrada (o número do card não muda)';
